import React, { useCallback, useEffect, useState } from "react";
import { Button, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import {
  RequisiteIProps,
  useActivateRequisite,
  useDeactivateRequisite,
  useGetRequisites,
} from "~/entity/AvtovazRequisites";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import DeleteRequisites from "./Modals/DeleteRequisites";
import RequisitesModalForm from "./Modals/RequisitesModalForm";
import {
  getRequisitesColumns,
  REQUISITES_SORTABLE_COLUMNS,
} from "./model/tableAdapter";
import { LIST_REQUISITES } from "./options";

const RequisitesPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { sortField, sortType: sortTypeRaw } = useGetLocationData();
  const { data, isLoading, refetch } = useGetRequisites();
  const { hasPermission } = usePermissions();
  const { mutate: mutateActivate, isSuccess: isSuccessActivate } =
    useActivateRequisite();
  const { mutate: mutateDeactivate, isSuccess: isSuccessDeactivate } =
    useDeactivateRequisite();

  const [pendingToggleId, setPendingToggleId] = useState<number | null>(null);

  // Эндпоинт реквизитов не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: REQUISITES_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && REQUISITES_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const handleToggleActive = useCallback(
    async (record: RequisiteIProps) => {
      const id = record.id;
      if (id === undefined) return;
      setPendingToggleId(id);
      try {
        if (record.active) {
          await mutateDeactivate(id);
        } else {
          await mutateActivate(id);
        }
      } finally {
        setPendingToggleId(null);
      }
    },
    [mutateActivate, mutateDeactivate],
  );

  useEffect(() => {
    if (isSuccessActivate || isSuccessDeactivate) {
      refetch();
    }
  }, [isSuccessActivate, isSuccessDeactivate]);

  const openCreateModal = () => setDataReplace("openModal", "Create");

  const handleRowEdit = (record: { id?: number }) =>
    setDataReplace("openModal", "Edit", "currentId", record.id);

  const handleRowDelete = (record: { id?: number }) =>
    setDataReplace("openModal", "Delete", "currentId", record.id);

  const goBack = () => history.replace("/editor/");

  const columns = getRequisitesColumns(
    LIST_REQUISITES,
    handleToggleActive,
    pendingToggleId,
    t,
    tableSortField,
    sortType,
  );

  return (
    <Wrapper.Content
      title={t("avtovaz_requisite.title")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      controlComponent={
        hasPermission("contracts.createVasReq") ? (
          <Button
            onClick={openCreateModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("ui.add")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        columns={columns}
        dataSource={data || []}
        rowKey="id"
        loading={!isLoading && !!!data}
        onRowEdit={handleRowEdit}
        onRowDelete={handleRowDelete}
        sortableColumns={REQUISITES_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
      />
      <RequisitesModalForm reFetchTable={refetch} />
      <DeleteRequisites reFetchTable={refetch} />
    </Wrapper.Content>
  );
};

export default RequisitesPage;
